import { type Option } from '@avanta/shared/components/ui/Select';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type Treatment } from '@avanta/shared/types/appointments';
import { type Speciality } from 'types/appointments';
import { type ContractRecordField } from 'types/enterprises';
import { type AppointmentsReserveFormData } from './types';

type RecordFieldValue = AppointmentsReserveFormData['recordFields'][string];

const isRecordFieldValue = (value: unknown): value is RecordFieldValue =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

export const hasRecordFieldValue = (value: RecordFieldValue | undefined): boolean => {
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value === 'string') return value.trim() !== '';

  return false;
};

export const areRecordFieldsComplete = (
  recordFields: ContractRecordField[],
  values: Record<string, RecordFieldValue>
): boolean =>
  recordFields
    .filter(recordField => recordField.active && recordField.required)
    .every(recordField => hasRecordFieldValue(values[recordField.key]));

export const getRecordFieldValues = (
  values?: Record<string, unknown>
): Record<string, RecordFieldValue> => {
  if (!values) return {};

  return Object.entries(values).reduce<Record<string, RecordFieldValue>>(
    (recordFieldValues, [key, value]) => {
      if (isRecordFieldValue(value)) {
        recordFieldValues[key] = value;
      }

      return recordFieldValues;
    },
    {}
  );
};

export const mapSpecialities = (specialities: Speciality[]): Option[] =>
  specialities.map(speciality => ({
    label: t(`AppointmentsReserve.Steps.speciality.options.${speciality.name}`, {
      ns: LOCIZE_NS.APPOINTMENTS,
    }),
    value: speciality.id,
  }));

export const mapTreatments = (
  specialityId: number | undefined,
  treatments: Treatment[]
): Option[] =>
  treatments
    .filter(treatment => treatment.speciality.id === Number(specialityId))
    .map(treatment => ({
      label: t(`AppointmentsReserve.Steps.treatment.options.${treatment.id}`, {
        ns: LOCIZE_NS.APPOINTMENTS,
      }),
      value: treatment.id,
    }));
